import { useState } from 'react';
import { Check, Plus, Trash2, X } from 'lucide-react';
import type { FkAction, ForeignKeyModel, SchemaModel, TableModel } from '@bits2schema/shared';
import { Modal } from '../../components/Modal';
import { useT } from '../../i18n';
import { areForeignKeyColumnTypesCompatible, defaultForeignKeyName, ensureUniqueName, isColumnSetKeyed } from '../mutations';

const FK_ACTIONS: FkAction[] = ['NO ACTION', 'RESTRICT', 'CASCADE', 'SET NULL', 'SET DEFAULT'];

interface ColumnPair {
  source: string;
  target: string;
}

export interface ForeignKeyDialogResult {
  name: string;
  columns: string[];
  referencedSchema: string;
  referencedTable: string;
  referencedColumns: string[];
  onDelete: FkAction;
  onUpdate: FkAction;
}

interface ForeignKeyDialogProps {
  schema: SchemaModel;
  table: TableModel;
  initial: ForeignKeyModel | null;
  onCancel: () => void;
  onConfirm: (result: ForeignKeyDialogResult) => void;
}

function initialPairs(initial: ForeignKeyModel | null): ColumnPair[] {
  if (!initial || initial.columns.length === 0) return [{ source: '', target: '' }];
  return initial.columns.map((source, index) => ({ source, target: initial.referencedColumns[index] ?? '' }));
}

function initialTargetTableId(schema: SchemaModel, initial: ForeignKeyModel | null): string {
  if (!initial) return '';
  return (
    schema.tables.find(
      (candidate) => candidate.schema === initial.referencedSchema && candidate.name === initial.referencedTable,
    )?.id ?? ''
  );
}

/**
 * Formulář FK vazby (E4-US8) — nejsložitější z dialogů: kompozitní páry
 * zdroj↔cíl (vazební tabulky M:N, kompozitní PK), self-reference (cílová
 * tabulka může být tatáž), typová kompatibilita jako blokující validace,
 * "cíl je součástí PK/unique" jako nezávazné upozornění (epic-4 kap. 7,
 * E4-US8 AC — "validace/upozornění", ne tvrdý blok).
 */
export function ForeignKeyDialog({ schema, table, initial, onCancel, onConfirm }: ForeignKeyDialogProps) {
  const t = useT();
  const [name, setName] = useState(initial?.name ?? '');
  const [targetTableId, setTargetTableId] = useState(initialTargetTableId(schema, initial));
  const [pairs, setPairs] = useState<ColumnPair[]>(initialPairs(initial));
  const [onDelete, setOnDelete] = useState<FkAction>(initial?.onDelete ?? 'NO ACTION');
  const [onUpdate, setOnUpdate] = useState<FkAction>(initial?.onUpdate ?? 'NO ACTION');

  const targetTable = schema.tables.find((candidate) => candidate.id === targetTableId) ?? null;

  const completePairs = pairs.filter((pair) => pair.source && pair.target);
  const hasIncompleteRow = pairs.some((pair) => Boolean(pair.source) !== Boolean(pair.target));
  const sourceNames = completePairs.map((pair) => pair.source);
  const hasDuplicateSource = new Set(sourceNames).size !== sourceNames.length;

  const typeMismatches = targetTable
    ? completePairs.filter((pair) => {
        const sourceColumn = table.columns.find((column) => column.name === pair.source);
        const targetColumn = targetTable.columns.find((column) => column.name === pair.target);
        if (!sourceColumn || !targetColumn) return false;
        return !areForeignKeyColumnTypesCompatible(sourceColumn, targetColumn);
      })
    : [];

  const targetIsKeyed =
    targetTable && completePairs.length > 0
      ? isColumnSetKeyed(targetTable, completePairs.map((pair) => pair.target))
      : true;

  const valid =
    Boolean(targetTable) && completePairs.length > 0 && !hasIncompleteRow && !hasDuplicateSource && typeMismatches.length === 0;

  const confirm = () => {
    if (!valid || !targetTable) return;
    const existingNames = table.foreignKeys.filter((candidate) => candidate.id !== initial?.id).map((candidate) => candidate.name);
    const columns = completePairs.map((pair) => pair.source);
    const finalName = name.trim() ? ensureUniqueName(name.trim(), existingNames) : defaultForeignKeyName(table, columns);
    onConfirm({
      name: finalName,
      columns,
      referencedSchema: targetTable.schema,
      referencedTable: targetTable.name,
      referencedColumns: completePairs.map((pair) => pair.target),
      onDelete,
      onUpdate,
    });
  };

  const updatePair = (index: number, patch: Partial<ColumnPair>) =>
    setPairs((previous) => previous.map((pair, candidateIndex) => (candidateIndex === index ? { ...pair, ...patch } : pair)));

  return (
    <Modal
      title={initial ? t('schema.dialog.fk.editTitle') : t('schema.dialog.fk.createTitle')}
      onClose={onCancel}
      wide
      actions={
        <>
          <button type="button" onClick={onCancel}>
            <X size={16} />
            {t('common.cancel')}
          </button>
          <button type="button" className="primary" disabled={!valid} onClick={confirm}>
            <Check size={16} />
            {initial ? t('schema.dialog.fk.editTitle') : t('common.create')}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label htmlFor="fk-name">{t('schema.dialog.fk.nameLabel')}</label>
          <input id="fk-name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="fk-target-table">{t('schema.dialog.fk.targetTableLabel')}</label>
          <select
            id="fk-target-table"
            value={targetTableId}
            onChange={(event) => setTargetTableId(event.target.value)}
          >
            <option value="" disabled>
              {t('schema.dialog.fk.targetTableLabel')}
            </option>
            {schema.tables.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.schema}.{candidate.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <span>{t('schema.dialog.fk.pairsLabel')}</span>
          <div className="stack">
            {pairs.map((pair, index) => (
              <div className="inline-fields" key={index}>
                <select value={pair.source} onChange={(event) => updatePair(index, { source: event.target.value })}>
                  <option value="" disabled>
                    {t('schema.dialog.fk.sourceColumnLabel')}
                  </option>
                  {table.columns.map((column) => (
                    <option key={column.id} value={column.name}>
                      {column.name}
                    </option>
                  ))}
                </select>
                <span aria-hidden="true">→</span>
                <select
                  value={pair.target}
                  onChange={(event) => updatePair(index, { target: event.target.value })}
                  disabled={!targetTable}
                >
                  <option value="" disabled>
                    {t('schema.dialog.fk.targetColumnLabel')}
                  </option>
                  {targetTable?.columns.map((column) => (
                    <option key={column.id} value={column.name}>
                      {column.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={pairs.length === 1}
                  onClick={() => setPairs(pairs.filter((_, candidateIndex) => candidateIndex !== index))}
                >
                  <Trash2 size={16} />
                  {t('schema.dialog.fk.removePair')}
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setPairs([...pairs, { source: '', target: '' }])}>
            <Plus size={16} />
            {t('schema.dialog.fk.addPair')}
          </button>

          {completePairs.length === 0 && <p className="hint">{t('schema.dialog.fk.pairsRequired')}</p>}
          {hasIncompleteRow && <p className="notice notice--error">{t('schema.dialog.fk.incompleteRow')}</p>}
          {hasDuplicateSource && (
            <p className="notice notice--error">
              {t('schema.dialog.fk.duplicateSourceColumn', { name: sourceNames.find((n, i) => sourceNames.indexOf(n) !== i) ?? '' })}
            </p>
          )}
          {typeMismatches.map((pair) => {
            const sourceColumn = table.columns.find((column) => column.name === pair.source);
            const targetColumn = targetTable?.columns.find((column) => column.name === pair.target);
            return (
              <p className="notice notice--error" key={`${pair.source}-${pair.target}`}>
                {t('schema.dialog.fk.typeMismatch', {
                  source: pair.source,
                  sourceType: sourceColumn?.canonicalType ?? sourceColumn?.rawType ?? '',
                  target: pair.target,
                  targetType: targetColumn?.canonicalType ?? targetColumn?.rawType ?? '',
                })}
              </p>
            );
          })}
          {valid && !targetIsKeyed && <p className="hint">{t('schema.dialog.fk.targetNotKeyWarning')}</p>}
        </div>

        <div className="inline-fields">
          <div className="field">
            <label htmlFor="fk-on-delete">{t('schema.dialog.fk.onDeleteLabel')}</label>
            <select id="fk-on-delete" value={onDelete} onChange={(event) => setOnDelete(event.target.value as FkAction)}>
              {FK_ACTIONS.map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="fk-on-update">{t('schema.dialog.fk.onUpdateLabel')}</label>
            <select id="fk-on-update" value={onUpdate} onChange={(event) => setOnUpdate(event.target.value as FkAction)}>
              {FK_ACTIONS.map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </Modal>
  );
}
